<template>
  <main class="message-report" aria-labelledby="report-title">
    <header class="report-header">
      <div>
        <div class="eyebrow">Communications ledger</div>
        <h1 id="report-title">SMS Message Report</h1>
        <p>Records saved by SaveAnimals after SMS requests are submitted to Telinfy.</p>
      </div>
      <span class="local-badge"><span aria-hidden="true"></span>Local send log</span>
    </header>

    <section class="summary-grid" aria-label="SMS summary">
      <article class="summary-card summary-total">
        <div class="summary-icon"><CIcon name="cil-comment-square" /></div>
        <div><span>Total SMS</span><strong>{{ formatNumber(summary.total) }}</strong><small>{{ sentRateText }}</small></div>
      </article>
      <article class="summary-card summary-sent">
        <div class="summary-icon"><CIcon name="cil-check-circle" /></div>
        <div><span>Sent</span><strong>{{ formatNumber(summary.sent) }}</strong><small>Accepted by Telinfy</small></div>
      </article>
      <article class="summary-card summary-failed">
        <div class="summary-icon"><CIcon name="cil-x-circle" /></div>
        <div><span>Failed</span><strong>{{ formatNumber(summary.failed) }}</strong><small>Request not accepted</small></div>
      </article>
      <article class="summary-card summary-quota">
        <div class="summary-icon"><CIcon name="cil-ban" /></div>
        <div><span>Quota Exceeded</span><strong>{{ formatNumber(summary.quotaExceeded) }}</strong><small>Skipped, balance was empty</small></div>
      </article>
      <article class="summary-card summary-units">
        <div class="summary-icon"><CIcon name="cil-calculator" /></div>
        <div><span>Units Used</span><strong>{{ formatNumber(summary.units) }}</strong><small>SMS billing units</small></div>
      </article>
    </section>

    <section class="balance-panel" aria-labelledby="balance-title">
      <div class="balance-heading">
        <div><h2 id="balance-title">SMS balance</h2><p>Prepaid unit pool shared across the whole platform.</p></div>
      </div>
      <div class="balance-grid">
        <div class="balance-cell">
          <span>Remaining</span>
          <strong>{{ formatNumber(quota.remainingUnits) }} <small class="unit-label">units</small></strong>
        </div>
        <div class="balance-cell">
          <span>Total Purchased</span>
          <strong>{{ formatNumber(quota.totalPurchased) }} <small class="unit-label">units</small></strong>
        </div>
        <div class="balance-cell">
          <span>Total Used</span>
          <strong>{{ formatNumber(quota.totalUsed) }} <small class="unit-label">units</small></strong>
        </div>
        <div class="balance-cell">
          <span>Total Recharged</span>
          <strong>₹{{ formatNumber(quota.totalAmountRecharged) }}</strong>
        </div>
        <div class="balance-cell">
          <span>Last Recharge</span>
          <strong v-if="quota.lastRecharge">₹{{ formatNumber(quota.lastRecharge.amount) }}</strong>
          <strong v-else>—</strong>
          <small v-if="quota.lastRecharge">{{ formatDateTime(quota.lastRecharge.createdAt) }} · +{{ formatNumber(quota.lastRecharge.unitsAdded) }} units</small>
          <small v-else>No recharge yet</small>
        </div>
      </div>
    </section>

    <section class="filter-panel" aria-labelledby="filter-title">
      <div class="panel-heading">
        <div><h2 id="filter-title">Find message records</h2><p>Refine the locally stored SMS activity.</p></div>
        <button type="button" class="text-action" :disabled="loading || exporting" @click="resetFilters">
          <CIcon name="cil-reload" /> Reset
        </button>
      </div>
      <form class="filter-grid" @submit.prevent="applyFilters">
        <div class="field field-search">
          <label for="report-search">Search</label>
          <div class="input-with-icon">
            <CIcon name="cil-magnifying-glass" />
            <input id="report-search" v-model.trim="filters.keyword" type="search" placeholder="Recipient, campaign, type or message" />
          </div>
        </div>
        <div class="field">
          <label for="report-source">Source</label>
          <select id="report-source" v-model="filters.source">
            <option value="">All sources</option><option value="marketing">Marketing</option><option value="notification">Notification</option>
          </select>
        </div>
        <div class="field">
          <label for="report-status">Status</label>
          <select id="report-status" v-model="filters.status">
            <option value="">All statuses</option><option value="sent">Sent</option><option value="failed">Failed</option><option value="quota_exceeded">Quota Exceeded</option>
          </select>
        </div>
        <div class="field">
          <label for="report-from">From</label>
          <input id="report-from" v-model="filters.startDate" type="date" />
        </div>
        <div class="field">
          <label for="report-to">To</label>
          <input id="report-to" v-model="filters.endDate" type="date" />
        </div>
        <div class="filter-actions">
          <button type="submit" class="btn-ledger btn-apply" :disabled="loading || exporting">
            <span v-if="loading" class="button-spinner" aria-hidden="true"></span>
            <CIcon v-else name="cil-magnifying-glass" /> {{ loading ? 'Loading' : 'Apply' }}
          </button>
          <button type="button" class="btn-ledger btn-export" :disabled="loading || exporting || total === 0" @click="exportReport('excel')">
            <CIcon name="cil-cloud-download" /> Excel
          </button>
          <button type="button" class="btn-ledger btn-export" :disabled="loading || exporting || total === 0" @click="exportReport('pdf')">
            <CIcon name="cil-file" /> PDF
          </button>
        </div>
      </form>
    </section>

    <section class="ledger-panel" aria-labelledby="ledger-title">
      <div class="ledger-heading">
        <div><h2 id="ledger-title">Message ledger</h2><p v-if="!loading && !error">{{ rangeText }}</p></div>
        <label class="page-size">Rows
          <select v-model.number="limit" :disabled="loading" @change="changeLimit"><option :value="10">10</option><option :value="25">25</option><option :value="50">50</option></select>
        </label>
      </div>

      <div v-if="loading" class="state-panel" role="status">
        <span class="ledger-loader" aria-hidden="true"></span><strong>Loading SMS records</strong><p>Reading the local SaveAnimals send log…</p>
      </div>
      <div v-else-if="error" class="state-panel state-error" role="alert">
        <CIcon name="cil-warning" /><strong>We couldn’t load the message report</strong><p>{{ error }}</p>
        <button type="button" class="btn-ledger btn-apply" @click="fetchReport">Try again</button>
      </div>
      <div v-else-if="rows.length === 0" class="state-panel">
        <CIcon name="cil-envelope-closed" /><strong>No SMS records found</strong><p>Try broadening the filters or changing the selected date range.</p>
      </div>
      <div v-else class="table-scroll">
        <table>
          <caption class="sr-only">SMS send records stored by SaveAnimals</caption>
          <thead><tr><th>Date &amp; Time</th><th>Source</th><th>Campaign / Type</th><th>Recipient</th><th>Message</th><th>Gateway Status</th><th>Gateway Message</th><th class="number-cell">Units</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id">
              <td class="date-cell"><strong>{{ row.date }}</strong><span>{{ row.time }}</span></td>
              <td><span class="source-label">{{ row.source }}</span></td>
              <td class="campaign-cell">{{ row.campaign }}</td>
              <td class="recipient-cell">{{ row.recipient }}</td>
              <td><span class="message-cell" :title="row.message" tabindex="0">{{ row.message }}</span><small v-if="row.error" class="row-error" :title="row.error">{{ row.error }}</small></td>
              <td><span class="gateway-status" :title="row.gatewayStatus">{{ row.gatewayStatus }}</span></td>
              <td><span class="gateway-message" :title="row.gatewayMessage" tabindex="0">{{ row.gatewayMessage }}</span></td>
              <td class="number-cell">{{ formatNumber(row.units) }}</td>
              <td><span class="status-pill" :class="statusClass(row.status)"><span></span>{{ row.statusLabel }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer v-if="!loading && !error && total > 0" class="pagination-bar">
        <span>{{ rangeText }}</span>
        <nav aria-label="Message report pages">
          <button type="button" :disabled="page <= 1" aria-label="Previous page" @click="goToPage(page - 1)"><CIcon name="cil-arrow-left" /></button>
          <span>Page <strong>{{ page }}</strong> of {{ totalPages }}</span>
          <button type="button" :disabled="page >= totalPages" aria-label="Next page" @click="goToPage(page + 1)"><CIcon name="cil-chevron-right" /></button>
        </nav>
      </footer>
    </section>

    <p class="report-note"><CIcon name="cil-info" /> Sent means Telinfy accepted the HTTP request; it does not confirm handset delivery.</p>

    <json-excel ref="excelDownload" v-show="false" :data="exportRows" :fields="excelFields" :name="exportFilename" />
  </main>
</template>

<script>
import axios from 'axios'
import JsonExcel from 'vue-json-excel'
import { jsPDF } from 'jspdf'
import store from '../store'

function localDateValue (date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function currentWeekRange () {
  const today = new Date()
  const weekStart = new Date(today)
  const daysSinceMonday = (today.getDay() + 6) % 7
  weekStart.setDate(today.getDate() - daysSinceMonday)

  return {
    startDate: localDateValue(weekStart),
    endDate: localDateValue(today)
  }
}

export default {
  name: 'MessageReport',
  components: { JsonExcel },
  data () {
    return {
      loading: false,
      exporting: false,
      error: '',
      rawRows: [],
      exportRows: [],
      page: 1,
      limit: 25,
      total: 0,
      totalPages: 1,
      summary: { total: 0, sent: 0, failed: 0, quotaExceeded: 0, units: 0 },
      filters: Object.assign({ keyword: '', source: '', status: '' }, currentWeekRange()),
      quotaLoading: false,
      quota: { remainingUnits: 0, totalPurchased: 0, totalUsed: 0, unitCost: 0.354, totalAmountRecharged: 0, lastRecharge: null },
      excelFields: {
        'Date & Time': 'Date & Time', Source: 'Source', 'Campaign / Type': 'Campaign / Type', Recipient: 'Recipient', Message: 'Message',
        'Gateway Status': 'Gateway Status', 'Gateway Message': 'Gateway Message',
        Length: 'Length', Segments: 'Segments', Encoding: 'Encoding', Units: 'Units', Status: 'Status', Error: 'Error'
      }
    }
  },
  computed: {
    rows () { return this.rawRows.map((row, index) => this.normalizeRow(row, index)) },
    sentRateText () {
      return this.summary.total > 0 ? `${Math.round((this.summary.sent / this.summary.total) * 100)}% sent rate` : 'No activity yet'
    },
    rangeText () {
      if (!this.total) return '0 records'
      const start = ((this.page - 1) * this.limit) + 1
      const end = Math.min(this.page * this.limit, this.total)
      return `${this.formatNumber(start)}–${this.formatNumber(end)} of ${this.formatNumber(this.total)} records`
    },
    exportFilename () { return `SaveAnimals_SMS_Report_${new Date().toISOString().slice(0, 10)}.xls` }
  },
  mounted () { this.fetchReport(); this.fetchQuota() },
  methods: {
    notify (type, title, text) { this.$notify({ group: 'notification', type, title, text }) },
    validDates () {
      if (this.filters.startDate && this.filters.endDate && this.filters.startDate > this.filters.endDate) {
        this.notify('error', 'Invalid date range', 'The From date must be before the To date.')
        return false
      }
      return true
    },
    queryParams (page, limit) {
      return {
        page, limit, keyword: this.filters.keyword, source: this.filters.source, channel: 'sms', status: this.filters.status,
        startDate: this.filters.startDate, endDate: this.filters.endDate
      }
    },
    fetchReport () {
      if (!this.validDates()) return
      this.loading = true
      this.error = ''
      return axios.get(`${store.state.baseURL}v1/admin/messages/report`, {
        headers: { authorization: store.state.token }, params: this.queryParams(this.page, this.limit)
      }).then(response => {
        const body = response && response.data ? response.data : {}
        if (body.status === false) throw new Error(body.message || 'The report service returned an error.')
        const data = body.data && typeof body.data === 'object' ? body.data : {}
        this.rawRows = Array.isArray(data.rows) ? data.rows : []
        this.total = this.numberValue(data.total, this.rawRows.length)
        this.page = Math.max(1, this.numberValue(data.page, this.page))
        this.limit = Math.max(1, this.numberValue(data.limit, this.limit))
        this.totalPages = Math.max(1, this.numberValue(data.totalPage, Math.ceil(this.total / this.limit)))
        this.summary = this.normalizeSummary(data.summary)
      }).catch(err => {
        this.rawRows = []
        this.total = 0
        this.totalPages = 1
        this.error = this.errorMessage(err)
      }).finally(() => {
        this.loading = false
      })
    },
    fetchQuota () {
      this.quotaLoading = true
      return axios.get(`${store.state.baseURL}v1/admin/messages/quota`, {
        headers: { authorization: store.state.token }
      }).then(response => {
        const body = response && response.data ? response.data : {}
        if (body.status === false) throw new Error(body.message || 'Could not load the SMS balance.')
        this.quota = this.normalizeQuota(body.data)
      }).catch(err => {
        this.notify('error', 'SMS balance', this.errorMessage(err))
      }).finally(() => {
        this.quotaLoading = false
      })
    },
    normalizeQuota (data) {
      const root = data && typeof data === 'object' ? data : {}
      return {
        remainingUnits: this.numberValue(root.remainingUnits, 0),
        totalPurchased: this.numberValue(root.totalPurchased, 0),
        totalUsed: this.numberValue(root.totalUsed, 0),
        unitCost: this.numberValue(root.unitCost, 0.354),
        totalAmountRecharged: this.numberValue(root.totalAmountRecharged, 0),
        lastRecharge: root.lastRecharge && typeof root.lastRecharge === 'object' ? root.lastRecharge : null
      }
    },
    formatDateTime (value) {
      const date = value ? new Date(value) : null
      return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : '—'
    },
    applyFilters () { this.page = 1; this.fetchReport() },
    resetFilters () {
      this.filters = Object.assign({ keyword: '', source: '', status: '' }, currentWeekRange())
      this.page = 1
      this.fetchReport()
    },
    changeLimit () { this.page = 1; this.fetchReport() },
    goToPage (page) {
      if (page < 1 || page > this.totalPages || page === this.page) return
      this.page = page
      this.fetchReport()
    },
    normalizeSummary (summary) {
      const root = summary && typeof summary === 'object' ? summary : {}
      const totals = root.totals && typeof root.totals === 'object' ? root.totals : {}
      const sms = root.byChannel && root.byChannel.sms && typeof root.byChannel.sms === 'object' ? root.byChannel.sms : {}
      const merged = Object.assign({}, totals, sms)
      return {
        total: this.pickNumber(merged, ['total', 'count', 'totalMessages', 'messages'], this.total),
        sent: this.pickNumber(merged, ['sent', 'sentCount', 'successful', 'success'], 0),
        failed: this.pickNumber(merged, ['failed', 'failedCount', 'failure'], 0),
        quotaExceeded: this.pickNumber(merged, ['quotaExceeded', 'quota_exceeded'], 0),
        units: this.pickNumber(merged, ['units', 'totalUnits', 'unitsUsed'], 0)
      }
    },
    pickNumber (object, keys, fallback) {
      for (const key of keys) if (object[key] !== undefined && Number.isFinite(Number(object[key]))) return Number(object[key])
      return fallback
    },
    numberValue (value, fallback) { return Number.isFinite(Number(value)) ? Number(value) : fallback },
    normalizeRow (row, index) {
      const source = this.titleCase(row && row.source ? row.source : 'Unknown')
      const status = row && row.status ? String(row.status).toLowerCase() : 'unknown'
      const rawDate = row && (row.sentAt || row.createdAt)
      const parsedDate = rawDate ? new Date(rawDate) : null
      const validDate = parsedDate && !Number.isNaN(parsedDate.getTime())
      return {
        id: (row && row._id) || `${rawDate || 'row'}-${index}`,
        dateTime: validDate ? parsedDate.toLocaleString() : '—',
        date: validDate ? parsedDate.toLocaleDateString() : '—',
        time: validDate ? parsedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
        source,
        campaign: this.campaignLabel(row),
        recipient: (row && row.recipient) || '—',
        message: (row && row.message) || '—',
        length: this.numberValue(row && row.length, 0), segments: this.numberValue(row && row.segments, 0),
        gatewayStatus: this.gatewayStatusLabel(row),
        gatewayMessage: this.gatewayMessageValue(row && row.gatewayResponse),
        encoding: (row && row.encoding) || '—', units: this.numberValue(row && row.units, 0), status,
        statusLabel: this.titleCase(status), error: (row && row.error) || ''
      }
    },
    campaignLabel (row) {
      if (!row) return '—'
      const campaign = row.title || (row.campaignId && (row.campaignId.name || row.campaignId.title || row.campaignId))
      return campaign || this.titleCase(row.notificationType || '—')
    },
    gatewayStatusLabel (row) {
      if (!row) return '—'
      const status = row.gatewayStatus || '—'
      return row.gatewayStatusCode ? `${status} (HTTP ${row.gatewayStatusCode})` : status
    },
    gatewayMessageValue (response, depth = 0) {
      if (response === undefined || response === null || response === '') return '—'

      if (typeof response === 'string') {
        const text = response.trim()
        if (!text) return '—'
        const looksLikeJSON = (text[0] === '{' && text[text.length - 1] === '}') || (text[0] === '[' && text[text.length - 1] === ']')
        if (depth < 2 && looksLikeJSON) {
          try {
            return this.gatewayMessageValue(JSON.parse(text), depth + 1)
          } catch (error) {
            return text
          }
        }
        return text
      }

      if (Array.isArray(response)) return response.length ? this.gatewayMessageValue(response[0], depth + 1) : '—'
      if (typeof response !== 'object') return String(response)

      const providerResponse = Array.isArray(response.responses) ? response.responses[0] : null
      if (providerResponse && providerResponse.message) return this.gatewayMessageValue(providerResponse.message, depth + 1)
      if (response.message) return this.gatewayMessageValue(response.message, depth + 1)
      if (response.error) return this.gatewayMessageValue(response.error, depth + 1)
      return '—'
    },
    titleCase (value) { return String(value).replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase()) },
    statusClass (status) { return status === 'sent' ? 'is-sent' : status === 'failed' ? 'is-failed' : status === 'quota_exceeded' ? 'is-quota' : 'is-unknown' },
    formatNumber (value) { return new Intl.NumberFormat().format(this.numberValue(value, 0)) },
    errorMessage (err) {
      return (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Please check your connection and try again.'
    },
    exportReport (format) {
      if (!this.validDates()) return
      this.exporting = true
      const exportLimit = Math.min(Math.max(this.total, this.limit, 1000), 5000)
      return axios.get(`${store.state.baseURL}v1/admin/messages/report`, {
        headers: { authorization: store.state.token }, params: this.queryParams(1, exportLimit)
      }).then(response => {
        const body = response && response.data ? response.data : {}
        if (body.status === false) throw new Error(body.message || 'The report could not be exported.')
        const records = body.data && Array.isArray(body.data.rows) ? body.data.rows : []
        if (!records.length) throw new Error('There are no matching SMS records to export.')
        this.exportRows = records.map((row, index) => this.exportRow(this.normalizeRow(row, index)))
        if (this.total > exportLimit) this.notify('warn', 'Export limited', `The export contains the first ${this.formatNumber(exportLimit)} matching records.`)
        if (format === 'excel') {
          return this.$nextTick().then(() => {
            if (!this.$refs.excelDownload || typeof this.$refs.excelDownload.generate !== 'function') throw new Error('The Excel exporter is not ready. Please try again.')
            this.$refs.excelDownload.generate()
          })
        }
        this.generatePDF(this.exportRows)
        return null
      }).then(() => {
        this.notify('success', 'Export ready', `${format === 'excel' ? 'Excel' : 'PDF'} report generated successfully.`)
      }).catch(err => {
        this.notify('error', 'Export failed', this.errorMessage(err))
      }).finally(() => {
        this.exporting = false
      })
    },
    exportRow (row) {
      return { 'Date & Time': row.dateTime, Source: row.source, 'Campaign / Type': row.campaign, Recipient: row.recipient, Message: row.message,
        'Gateway Status': row.gatewayStatus, 'Gateway Message': row.gatewayMessage,
        Length: row.length, Segments: row.segments, Encoding: row.encoding, Units: row.units, Status: row.statusLabel, Error: row.error }
    },
    generatePDF (records) {
      const doc = new jsPDF('landscape', 'mm', 'a4')
      const columns = [
        ['Date & Time', 24], ['Source', 14], ['Campaign / Type', 22], ['Recipient', 22], ['Message', 38],
        ['Gateway Status', 17], ['Gateway Message', 40], ['Length', 9], ['Segments', 10], ['Encoding', 12],
        ['Units', 8], ['Status', 12], ['Error', 31]
      ]
      const left = 10
      const drawHeader = () => {
        doc.setFillColor(23, 63, 53); doc.rect(0, 0, 297, 24, 'F')
        doc.setTextColor(255, 255, 255); doc.setFontSize(15); doc.text('SaveAnimals SMS Message Report', left, 11)
        doc.setFontSize(8); doc.text(`Local send log • Generated ${new Date().toLocaleString()}`, left, 17)
        let x = left; doc.setFillColor(232, 239, 235); doc.rect(left, 27, 269, 8, 'F'); doc.setTextColor(23, 63, 53); doc.setFontSize(6.5)
        columns.forEach(column => { doc.text(column[0], x + 1, 32); x += column[1] })
      }
      drawHeader()
      let y = 36
      records.forEach(record => {
        const values = columns.map(column => doc.splitTextToSize(String(record[column[0]] === undefined ? '' : record[column[0]]), column[1] - 2).slice(0, 3))
        const height = Math.max(7, Math.max(...values.map(value => value.length)) * 3.2 + 2)
        if (y + height > 195) { doc.addPage(); drawHeader(); y = 36 }
        let x = left
        doc.setDrawColor(220, 228, 223); doc.setTextColor(38, 51, 47); doc.setFontSize(6.4)
        values.forEach((value, index) => { doc.text(value, x + 1, y + 3); doc.line(x, y + height, x + columns[index][1], y + height); x += columns[index][1] })
        y += height
      })
      doc.save(`SaveAnimals_SMS_Report_${new Date().toISOString().slice(0, 10)}.pdf`)
    }
  }
}
</script>

<style scoped>
.message-report { --forest:#173f35; --forest-2:#245c4d; --green:#2e7d5b; --paper:#fffefa; --mist:#f5f7f4; --amber:#d9902f; --red:#b44343; --ink:#26332f; padding:20px; color:var(--ink); }
.report-header { display:flex; align-items:flex-start; justify-content:space-between; gap:24px; margin-bottom:20px; padding:24px 26px; border-left:5px solid var(--green); background:var(--paper); box-shadow:0 5px 18px rgba(23,63,53,.07); }
.eyebrow { margin-bottom:4px; color:var(--green); font-size:11px; font-weight:800; letter-spacing:.13em; text-transform:uppercase; }
.report-header h1 { margin:0 0 7px; color:var(--forest); font-size:29px; font-weight:700; letter-spacing:-.025em; }
.report-header p { max-width:670px; margin:0; color:#60706a; font-size:14px; }
.local-badge { display:inline-flex; flex:0 0 auto; align-items:center; gap:7px; margin-top:4px; padding:6px 9px; border:1px solid #cadad3; border-radius:3px; background:#edf4f0; color:var(--forest-2); font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; }
.local-badge span { width:7px; height:7px; border-radius:50%; background:var(--green); box-shadow:0 0 0 3px rgba(46,125,91,.13); }
.summary-grid { display:grid; grid-template-columns:repeat(5,minmax(0,1fr)); gap:12px; margin-bottom:16px; }
.summary-card { position:relative; display:flex; min-height:112px; align-items:center; gap:15px; padding:18px; overflow:hidden; border:1px solid #dce5e0; border-top:3px solid var(--forest-2); border-radius:4px; background:var(--paper); box-shadow:0 3px 11px rgba(38,51,47,.045); }
.summary-card::after { position:absolute; right:-18px; bottom:-31px; width:74px; height:74px; border:13px solid rgba(46,125,91,.05); border-radius:50%; content:""; }
.summary-icon { display:grid; width:40px; height:40px; flex:0 0 40px; place-items:center; border-radius:3px; background:#e9f1ed; color:var(--forest-2); font-size:20px; }
.summary-card span { display:block; color:#64736d; font-size:11px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; }
.summary-card strong { display:block; margin:2px 0 1px; color:var(--forest); font-size:27px; line-height:1.1; }
.summary-card small { color:#78857f; font-size:11px; }.summary-sent{border-top-color:var(--green)}.summary-failed{border-top-color:var(--red)}.summary-failed .summary-icon{background:#f8eaea;color:var(--red)}.summary-quota{border-top-color:#946bc4}.summary-quota .summary-icon{background:#f0e9f7;color:#946bc4}.summary-units{border-top-color:var(--amber)}.summary-units .summary-icon{background:#fbf0df;color:#a76510}
.filter-panel,.ledger-panel { margin-bottom:16px; border:1px solid #d9e2dd; border-radius:4px; background:var(--paper); box-shadow:0 4px 16px rgba(38,51,47,.045); }
.panel-heading,.ledger-heading { display:flex; align-items:center; justify-content:space-between; gap:18px; padding:16px 18px; border-bottom:1px solid #e3e9e5; }
.panel-heading h2,.ledger-heading h2 { margin:0 0 2px; color:var(--forest); font-size:16px; font-weight:700; }.panel-heading p,.ledger-heading p{margin:0;color:#75827d;font-size:12px}
.text-action { border:0; background:transparent; color:var(--green); font-size:12px; font-weight:700; }.text-action:disabled{opacity:.5}.text-action:focus,.btn-ledger:focus,input:focus,select:focus{outline:3px solid rgba(46,125,91,.18);outline-offset:1px}
.filter-grid { display:grid; box-sizing:border-box; width:100%; grid-template-columns:minmax(0,2fr) repeat(4,minmax(0,1fr)); gap:14px; padding:17px 18px 19px; }
.field,.filter-actions { min-width:0; }
.field label { display:block; margin-bottom:5px; color:#53625c; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.035em; }
.field input,.field select,.page-size select { box-sizing:border-box; width:100%; height:39px; border:1px solid #cad6d0; border-radius:3px; background:#fff; color:var(--ink); font-size:13px; }
.field input,.field select{max-width:100%;padding:7px 9px}.input-with-icon{position:relative;min-width:0}.input-with-icon svg{position:absolute;top:12px;left:10px;color:#718079}.input-with-icon input{padding-left:32px}
.filter-actions { display:flex; grid-column:1/-1; justify-content:flex-end; gap:8px; padding-top:2px; }
.btn-ledger { min-height:38px; padding:8px 14px; border:1px solid transparent; border-radius:3px; font-size:12px; font-weight:700; transition:background .15s ease,transform .15s ease,box-shadow .15s ease; }.btn-ledger:not(:disabled):hover{transform:translateY(-1px);box-shadow:0 3px 8px rgba(23,63,53,.15)}.btn-ledger:disabled{cursor:not-allowed;opacity:.52}
.btn-apply{background:var(--forest-2);color:#fff}.btn-apply:hover{background:var(--forest)}.btn-export{border-color:#b9cbc2;background:#fff;color:var(--forest-2)}.btn-export:hover{background:#eef4f1}
.button-spinner,.ledger-loader{display:inline-block;border:2px solid rgba(255,255,255,.45);border-top-color:#fff;border-radius:50%;animation:spin .7s linear infinite}.button-spinner{width:13px;height:13px;vertical-align:-2px}.ledger-loader{width:27px;height:27px;margin-bottom:10px;border-color:#d2ded8;border-top-color:var(--green)}@keyframes spin{to{transform:rotate(360deg)}}
.page-size { display:flex; align-items:center; gap:8px; margin:0; color:#6b7973; font-size:12px; }.page-size select{width:62px;height:32px;padding:3px 6px}
.table-scroll{overflow-x:auto}table{width:100%;min-width:1350px;border-collapse:collapse}th{padding:10px 13px;border-bottom:2px solid #c9d8d0;background:var(--mist);color:#53645d;font-size:10px;font-weight:800;letter-spacing:.055em;text-align:left;text-transform:uppercase}td{padding:12px 13px;border-bottom:1px solid #e5eae7;color:#35423d;font-size:12px;vertical-align:top}tbody tr{transition:background .15s ease}tbody tr:hover{background:#f6faf7}tbody tr:last-child td{border-bottom:0}.date-cell{white-space:nowrap}.date-cell strong,.date-cell span{display:block}.date-cell strong{font-weight:600}.date-cell span{margin-top:2px;color:#7a8782;font-size:11px}.source-label{color:var(--forest-2);font-size:11px;font-weight:700}.campaign-cell{max-width:165px;font-weight:600}.recipient-cell{font-variant-numeric:tabular-nums;white-space:nowrap}.message-cell,.gateway-message{display:-webkit-box;max-width:350px;overflow:hidden;-webkit-box-orient:vertical;-webkit-line-clamp:2;line-height:1.45}.gateway-message{max-width:290px;color:#586761;font-size:11px}.gateway-status{white-space:nowrap;font-weight:700}.message-cell:focus,.gateway-message:focus{outline:2px solid rgba(46,125,91,.3)}.row-error{display:block;max-width:350px;margin-top:4px;overflow:hidden;color:var(--red);font-size:10px;text-overflow:ellipsis;white-space:nowrap}.number-cell{text-align:right;font-variant-numeric:tabular-nums}.status-pill{display:inline-flex;align-items:center;gap:6px;padding:4px 7px;border-radius:3px;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.035em}.status-pill span{width:6px;height:6px;border-radius:50%;background:currentColor}.is-sent{background:#e5f1ea;color:#24704f}.is-failed{background:#f8e5e5;color:#a33939}.is-quota{background:#f0e9f7;color:#946bc4}.is-unknown{background:#ecefed;color:#63716b}
.state-panel{display:flex;min-height:250px;align-items:center;justify-content:center;flex-direction:column;padding:35px;text-align:center;color:#708079}.state-panel>svg{margin-bottom:10px;color:#8aa097;font-size:28px}.state-panel strong{color:var(--forest);font-size:15px}.state-panel p{max-width:460px;margin:5px 0 12px;font-size:12px}.state-error>svg,.state-error strong{color:var(--red)}
.pagination-bar{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-top:1px solid #e1e8e4;background:#fbfcfa;color:#64726c;font-size:12px}.pagination-bar nav{display:flex;align-items:center;gap:10px}.pagination-bar button{display:grid;width:32px;height:30px;place-items:center;border:1px solid #c8d5cf;border-radius:3px;background:#fff;color:var(--forest-2)}.pagination-bar button:disabled{opacity:.35}.pagination-bar button:not(:disabled):hover{background:#eaf2ee}
.report-note{display:flex;align-items:center;gap:8px;margin:5px 2px 0;color:#687770;font-size:11px}.report-note svg{color:var(--amber)}
@media(max-width:1100px){.summary-grid{grid-template-columns:repeat(3,1fr)}.filter-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.field-search{grid-column:1/-1}}
@media(max-width:820px){.summary-grid{grid-template-columns:repeat(2,1fr)}}
@media(max-width:640px){.message-report{padding:12px}.report-header{padding:19px;flex-direction:column}.report-header h1{font-size:24px}.local-badge{margin-top:0}.summary-grid{grid-template-columns:1fr}.summary-card{min-height:96px}.filter-grid{grid-template-columns:1fr}.field-search{grid-column:auto}.filter-actions{grid-column:auto;flex-wrap:wrap}.filter-actions .btn-apply{width:100%}.filter-actions .btn-export{flex:1}.ledger-heading{align-items:flex-start}.pagination-bar{align-items:flex-start;flex-direction:column;gap:10px}.report-note{align-items:flex-start}}
@media(prefers-reduced-motion:reduce){.button-spinner,.ledger-loader{animation:none}.btn-ledger,tbody tr{transition:none}}
.balance-panel{margin-bottom:16px;border:1px solid #d9e2dd;border-radius:4px;background:var(--paper);box-shadow:0 4px 16px rgba(38,51,47,.045)}
.balance-heading{display:flex;align-items:center;justify-content:space-between;gap:18px;padding:16px 18px;border-bottom:1px solid #e3e9e5}
.balance-heading h2{margin:0 0 2px;color:var(--forest);font-size:16px;font-weight:700}.balance-heading p{margin:0;color:#75827d;font-size:12px}
.balance-grid{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:14px;padding:18px}
.balance-cell{display:flex;flex-direction:column;gap:3px}
.balance-cell span{color:#64736d;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase}
.balance-cell strong{color:var(--forest);font-size:22px;font-weight:700;line-height:1.2}
.balance-cell small{color:#78857f;font-size:11px}
.unit-label{color:#78857f;font-size:12px;font-weight:600;text-transform:none;letter-spacing:0}
@media(max-width:1100px){.balance-grid{grid-template-columns:repeat(3,1fr)}}
@media(max-width:640px){.balance-heading{align-items:flex-start;flex-direction:column}.balance-grid{grid-template-columns:1fr}}
</style>
